import React from 'react';
import { GoogleMap, Marker, Polyline } from '@react-google-maps/api';
import { Spin, Tooltip } from 'antd';
import { MapPin, Clock, Car, Store, Crosshair, Map as MapIcon, Ruler } from 'lucide-react';
import dayjs from 'dayjs';
import { SectionCard, EmptyState, StatusTag } from '../../common';
import { brand } from '../../../theme/tokens';
import { getGpsAccuracy, GPS_COLORS } from './gps';
import {
    STORE_MARKER_COLOR,
    useRouteEfficiencyMap,
    getMarkerColor,
    getDeviationLineColor,
    getAuditPosition,
    getStorePosition,
    formatCoord,
    createAuditMarkerIcon,
    createStoreMarkerIcon
} from './routeEfficiencyMap.controller';

import { BASE_MAP_OPTIONS, DEFAULT_MAP_CENTER as DEFAULT_CENTER, useShelfMapsLoader } from '../../common/map/mapTheme';

/** One legend entry on the map overlay. */
const LegendItem = ({ swatch, children }) => (
    <div className="flex items-center gap-2">
        {swatch}
        <span className="text-[11px] text-surface-600">{children}</span>
    </div>
);

const Dot = ({ color }) => <span className="h-3 w-3 shrink-0 rounded-full" style={{ backgroundColor: color }} />;

/** Coordinate tile for the selected visit. */
const CoordTile = ({ icon, label, children, className = '' }) => (
    <div className={`rounded-lg border border-surface-200 bg-surface-25 px-3 py-2 ${className}`}>
        <div className="mb-1 flex items-center gap-1.5 text-xs font-medium text-surface-500">
            {icon}
            {label}
        </div>
        {children}
    </div>
);

function RouteEfficiencyMap({ visits = [], selectedIndex = null, onSelectVisit }) {
    const { mappableVisits, routePath, deviationLines, focusVisit, onMapLoad } = useRouteEfficiencyMap({ visits, onSelectVisit });
    const { isLoaded } = useShelfMapsLoader();

    if (!visits.length) {
        return (
            <div className="shelf-card">
                <EmptyState icon={<MapIcon />} title="Mapa de ruta" description="Sin visitas para mostrar en el mapa" />
            </div>
        );
    }

    if (!mappableVisits.length) {
        return (
            <div className="shelf-card">
                <EmptyState
                    icon={<MapIcon />}
                    title="Mapa de ruta"
                    description="Las visitas no tienen coordenadas GPS ni ubicación teórica del PDV"
                />
            </div>
        );
    }

    const selectedVisit = selectedIndex != null ? visits[selectedIndex] : null;

    return (
        <SectionCard
            flush
            icon={<MapIcon />}
            title="Mapa de ruta"
            description="Cuadrado índigo = PDV registrado · Círculo = GPS del storecheck · Línea punteada = desviación"
            extra={
                <>
                    <StatusTag tone="brand" dot>
                        Ruta (storecheck)
                    </StatusTag>
                    <StatusTag tone="violet" dot>
                        PDV
                    </StatusTag>
                </>
            }>
            {selectedVisit && (getStorePosition(selectedVisit) || getAuditPosition(selectedVisit)) && (
                <div className="grid gap-3 border-b border-surface-100 px-5 py-3 sm:grid-cols-3">
                    <CoordTile icon={<Store className="text-violet-fg" />} label="Coord. PDV">
                        <span className="block font-mono text-xs tabular-nums text-surface-900">
                            {formatCoord(getStorePosition(selectedVisit))}
                        </span>
                    </CoordTile>
                    <CoordTile icon={<Crosshair className="text-success-fg" />} label="Coord. storecheck">
                        <span className="block font-mono text-xs tabular-nums text-surface-900">
                            {formatCoord(getAuditPosition(selectedVisit))}
                        </span>
                    </CoordTile>
                    <CoordTile icon={<Ruler />} label="Distancia">
                        {selectedVisit.distanceMeters != null ? (
                            <StatusTag tone={getGpsAccuracy(selectedVisit.distanceMeters).tone} dot className="tabular-nums">
                                {selectedVisit.distanceMeters} m
                            </StatusTag>
                        ) : (
                            <span className="text-xs text-surface-400">Sin comparación</span>
                        )}
                    </CoordTile>
                </div>
            )}

            <div className="flex flex-col xl:flex-row">
                <div className="relative flex-1 min-h-[420px] xl:min-h-[520px]">
                    {!isLoaded ? (
                        <div className="flex h-full min-h-[420px] items-center justify-center bg-surface-50">
                            <Spin />
                        </div>
                    ) : (
                        <GoogleMap
                            mapContainerStyle={{ width: '100%', height: '100%', minHeight: '420px' }}
                            center={routePath[0] || DEFAULT_CENTER}
                            zoom={11}
                            options={BASE_MAP_OPTIONS}
                            onLoad={onMapLoad}>
                            {routePath.length > 1 && (
                                <Polyline
                                    path={routePath}
                                    options={{
                                        strokeColor: brand[600],
                                        strokeOpacity: 0.85,
                                        strokeWeight: 3,
                                        geodesic: true,
                                        icons: window.google?.maps
                                            ? [
                                                  {
                                                      icon: {
                                                          path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
                                                          scale: 2.5,
                                                          strokeColor: brand[700],
                                                          fillColor: brand[700],
                                                          fillOpacity: 1
                                                      },
                                                      offset: '0',
                                                      repeat: '100px'
                                                  }
                                              ]
                                            : undefined
                                    }}
                                />
                            )}

                            {deviationLines.map((line) => (
                                <Polyline
                                    key={`deviation-${line.index}`}
                                    path={line.path}
                                    options={{
                                        strokeColor: getDeviationLineColor(line.distanceMeters),
                                        strokeOpacity: selectedIndex === line.index ? 1 : 0.7,
                                        strokeWeight: selectedIndex === line.index ? 3 : 2,
                                        geodesic: true,
                                        icons: window.google?.maps
                                            ? [
                                                  {
                                                      icon: { path: 'M 0,-1 0,1', strokeOpacity: 1, scale: 3 },
                                                      offset: '0',
                                                      repeat: '10px'
                                                  }
                                              ]
                                            : undefined
                                    }}
                                />
                            ))}

                            {mappableVisits.map(({ visit, index, storePosition, auditPosition }) => (
                                <React.Fragment key={visit.storeCheckId || index}>
                                    {storePosition && (
                                        <Marker
                                            position={storePosition}
                                            icon={createStoreMarkerIcon(index + 1, selectedIndex === index)}
                                            onClick={() => focusVisit(index)}
                                            zIndex={selectedIndex === index ? 900 : index}
                                            title={`PDV: ${visit.store?.name || 'Piso de venta'}`}
                                        />
                                    )}
                                    {auditPosition && (
                                        <Marker
                                            position={auditPosition}
                                            icon={createAuditMarkerIcon(index + 1, getMarkerColor(visit.distanceMeters), selectedIndex === index)}
                                            onClick={() => focusVisit(index)}
                                            zIndex={selectedIndex === index ? 1000 : index + 50}
                                            title={`Storecheck: ${visit.store?.name || 'Auditoría'}`}
                                        />
                                    )}
                                </React.Fragment>
                            ))}
                        </GoogleMap>
                    )}

                    <div className="absolute bottom-4 left-4 z-10 max-w-[240px] rounded-lg border border-surface-200 bg-white/95 px-3 py-2 shadow-sm">
                        <div className="mb-1.5 text-[11px] font-semibold text-surface-700">Leyenda</div>
                        <div className="flex flex-col gap-1.5">
                            <LegendItem swatch={<span className="h-3 w-3 shrink-0 rounded-sm" style={{ backgroundColor: STORE_MARKER_COLOR }} />}>
                                PDV registrado
                            </LegendItem>
                            <LegendItem swatch={<Dot color={GPS_COLORS.precise} />}>Storecheck ≤ 100 m</LegendItem>
                            <LegendItem swatch={<Dot color={GPS_COLORS.moderate} />}>Storecheck 101–500 m</LegendItem>
                            <LegendItem swatch={<Dot color={GPS_COLORS.deviated} />}>Storecheck &gt; 500 m</LegendItem>
                            <LegendItem swatch={<span className="w-6 shrink-0 border-t-2 border-dashed border-surface-400" />}>PDV ↔ storecheck</LegendItem>
                        </div>
                    </div>
                </div>

                <div className="flex max-h-[420px] flex-col border-t border-surface-100 bg-surface-25 xl:max-h-[520px] xl:w-[360px] xl:border-l xl:border-t-0">
                    <div className="border-b border-surface-100 bg-white px-4 py-3 text-sm font-semibold text-surface-900">Timeline de visitas</div>
                    <div className="flex-1 space-y-1.5 overflow-y-auto p-3">
                        {visits.map((visit, index) => {
                            const gps = getGpsAccuracy(visit.distanceMeters);
                            const isSelected = selectedIndex === index;
                            const storePosition = getStorePosition(visit);
                            const auditPosition = getAuditPosition(visit);
                            const hasCoords = storePosition != null || auditPosition != null;

                            return (
                                <button
                                    key={visit.storeCheckId || index}
                                    type="button"
                                    onClick={() => focusVisit(index)}
                                    disabled={!hasCoords}
                                    className={`w-full rounded-lg border p-3 text-left transition-colors ${
                                        isSelected ? 'border-primary-200 bg-primary-50' : 'border-surface-200 bg-white hover:border-surface-300'
                                    } ${!hasCoords ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
                                    <div className="flex items-start gap-3">
                                        <div className="flex shrink-0 flex-col items-center gap-0.5">
                                            <div
                                                className="flex h-6 w-6 items-center justify-center rounded text-[10px] font-bold text-white"
                                                style={{ backgroundColor: STORE_MARKER_COLOR }}>
                                                {index + 1}
                                            </div>
                                            <div
                                                className="flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-bold text-white"
                                                style={{ backgroundColor: getMarkerColor(visit.distanceMeters) }}>
                                                {index + 1}
                                            </div>
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <div className="truncate text-sm font-medium text-surface-900">{visit.store?.name || 'PDV desconocido'}</div>
                                            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-surface-500 tabular-nums">
                                                <span className="flex items-center gap-1">
                                                    <Clock />
                                                    {visit.startTime ? dayjs(visit.startTime).format('HH:mm') : '—'}
                                                    {visit.endTime ? ` – ${dayjs(visit.endTime).format('HH:mm')}` : ''}
                                                </span>
                                                {visit.transitMinutes != null && index > 0 && (
                                                    <Tooltip title="Tránsito desde visita anterior">
                                                        <span className="flex items-center gap-1">
                                                            <Car />+{visit.transitMinutes} min
                                                        </span>
                                                    </Tooltip>
                                                )}
                                            </div>

                                            <div className="mt-2 space-y-1">
                                                <div className="flex items-start gap-1.5 text-[11px]">
                                                    <Store className="mt-px text-violet-fg" />
                                                    <span className="font-mono leading-tight text-surface-600 tabular-nums">{formatCoord(storePosition)}</span>
                                                </div>
                                                <div className="flex items-start gap-1.5 text-[11px]">
                                                    <Crosshair className="mt-px text-success-fg" />
                                                    <span className="font-mono leading-tight text-surface-600 tabular-nums">{formatCoord(auditPosition)}</span>
                                                </div>
                                            </div>

                                            <div className="mt-2">
                                                <Tooltip title={gps.description}>
                                                    <span>
                                                        <StatusTag tone={gps.tone} icon={<MapPin />} className="tabular-nums">
                                                            {gps.label}
                                                        </StatusTag>
                                                    </span>
                                                </Tooltip>
                                            </div>
                                        </div>
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>
        </SectionCard>
    );
}

export default RouteEfficiencyMap;
